import { withProductFeature } from "../../product-access/ProductAccessContext";
import React, { Component } from "react";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  Badge,
  Row,
  Col,
} from "reactstrap";
import { Link } from "react-router-dom";
import ClampedMarkdown from "../Common/ClampedMarkdown";
import moment from "moment-timezone";
import "moment/locale/fr";
import { SiteDatabaseConnector } from "../../dataconnector/siteDatabaseConnector";
import { getZooId } from "../../tools/conf";
import { initDb } from "../../tools/conf";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
} from "../../anonymization/modularFirestore";
import "./TaskNotificationDropdown.css";
import { TASK_STATUS_COLORS } from "../../constants/taskStatusColors";
import { nowInZoo } from "../../utils/timezoneHelper";

// Timezone is now managed dynamically via zoo settings

const siteConnect = new SiteDatabaseConnector();

class TaskNotificationDropdown extends Component {
  state = {
    tasks: [],
    sectors: [],
    loading: true,
    dropdownOpen: false,
    stats: {
      total: 0,
      open: 0,
      inProgress: 0,
      overdue: 0,
      dueToday: 0,
    },
    groupedTasks: {},
    listener: null,
    expandedSectors: {}, // Track which sectors are expanded
  };

  toggleDropdown = () => {
    const wasOpen = this.state.dropdownOpen;
    this.setState((prevState) => ({
      dropdownOpen: !prevState.dropdownOpen,
    }));
    // Reload data when opening to pick up any preference changes
    if (!wasOpen) {
      this.loadInitialData();
    }
  };

  closeDropdown = () => {
    this.setState({ dropdownOpen: false });
  };

  toggleSectorExpansion = (sectorName) => {
    this.setState((prevState) => ({
      expandedSectors: {
        ...prevState.expandedSectors,
        [sectorName]: !prevState.expandedSectors[sectorName],
      },
    }));
  };

  async componentDidMount() {
    await this.loadInitialData();
    this.setupRealtimeListener();
    // Listen for user preference changes (e.g. taskNotificationMode toggle)
    this._onUserDataUpdated = () => this.loadInitialData();
    window.addEventListener("userDataUpdated", this._onUserDataUpdated);
  }

  componentWillUnmount() {
    // Clean up the Firestore listener
    if (this.state.listener) {
      this.state.listener();
    }
    if (this._reloadTimer) clearTimeout(this._reloadTimer);
    // Clean up user data listener
    if (this._onUserDataUpdated) {
      window.removeEventListener("userDataUpdated", this._onUserDataUpdated);
    }
  }

  setupRealtimeListener = async () => {
    try {
      const db = initDb();
      const zooId = await getZooId();

      // Try different ways to get userId
      let userId = null;
      const userDataString = localStorage.getItem("userData");
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          userId = userData.userId || userData.uid || userData.id;
        } catch (e) {
          console.error("[TaskNotification] Error parsing userData:", e);
        }
      }

      // Fallback to old method
      if (!userId) {
        userId = localStorage.getItem("userId") || localStorage.getItem("uid");
      }

      if (!userId) {
        console.error("[TaskNotification] No user ID found for listener");
        return;
      }

      // Écoute bornée aux tâches ACTIVES du parc (décision du 2026-09-05).
      // L'ancienne écoute portait sur toutes les tâches non archivées, donc sur
      // tout l'historique DONE/FAILED/CANCELLED : à chaque rechargement de
      // document (dont chaque passage V1↔V2) et à chaque écriture sur une tâche
      // quelconque, la cloche retéléchargeait des milliers de documents — trois
      // fois (écoute + deux requêtes complètes) — en saturant le canal Firestore
      // partagé par toutes les autres lectures de la page.
      // Une tâche qui se clôture quitte cet ensemble : l'événement est donc
      // toujours reçu, et le rechargement (borné) relit les clôtures du jour.
      const tasksCollection = collection(db, "tasks");
      const q = query(
        tasksCollection,
        where("zooId", "==", zooId),
        where("status", "in", ["OPEN", "IN_PROGRESS"])
      );

      let initialSnapshotSkipped = false;
      const listener = onSnapshot(q, () => {
        // Le premier événement rejoue l'état déjà chargé par componentDidMount.
        if (!initialSnapshotSkipped) {
          initialSnapshotSkipped = true;
          return;
        }
        this.scheduleReload();
      });

      this.setState({ listener });
    } catch (error) {
      console.error("Error setting up realtime listener:", error);
    }
  };

  // Regroupe les rafales d'événements (automatisations créant plusieurs
  // tâches d'un coup) en un seul rechargement.
  scheduleReload = () => {
    if (this._reloadTimer) clearTimeout(this._reloadTimer);
    this._reloadTimer = setTimeout(() => {
      this._reloadTimer = null;
      this.loadInitialData();
    }, 800);
  };

  loadInitialData = async () => {
    try {
      const zooId = await getZooId();
      // Try different ways to get userId
      let userId = null;
      const userDataString = localStorage.getItem("userData");
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          userId = userData.userId || userData.uid || userData.id;
        } catch (e) {
          console.error("[TaskNotification] Error parsing userData:", e);
        }
      }

      // Fallback to old method
      if (!userId) {
        userId = localStorage.getItem("userId") || localStorage.getItem("uid");
      }

      console.log(
        "[TaskNotification] Loading tasks for user:",
        userId,
        "zoo:",
        zooId
      );

      if (!userId) {
        console.error("[TaskNotification] No user ID found in localStorage");
        this.setState({ loading: false });
        return;
      }

      // Load sectors - exclude archived
      const sectorsData = (await siteConnect._getSectorIndexFromZooId(zooId))
        .filter(s => !s.isArchived);

      // Log user's sectors
      const userSectorsDebug = sectorsData.filter(
        (sector) =>
          sector.sectorKeeper?.includes(userId) ||
          sector.managementUserIds?.includes(userId) ||
          sector.veterinaryUserIds?.includes(userId) ||
          sector.nutritionUserIds?.includes(userId)
      );

      console.log(
        "[TaskNotification] User sectors:",
        userSectorsDebug.map((s) => ({
          id: s.sectorId,
          name: s.sectorName,
          roles: {
            keeper: s.sectorKeeper?.includes(userId),
            management: s.managementUserIds?.includes(userId),
            veterinary: s.veterinaryUserIds?.includes(userId),
            nutrition: s.nutritionUserIds?.includes(userId),
          },
        }))
      );

      // Lectures bornées à ce que la cloche affiche réellement
      // (tasks_time_guidelines.md, décision du 2026-09-05) :
      // - OPEN/IN_PROGRESS : toutes, quelle que soit la date ;
      // - DONE : clôturées aujourd'hui (completedAt ≥ début du jour parc) ;
      // - FAILED : échouées aujourd'hui (failedAt ≥ début du jour parc).
      // Le filtrage ci-dessous reste inchangé : il ne conservait déjà que ces
      // trois ensembles. Index utilisés : (zooId, status),
      // (status, zooId, completedAt desc), (status, zooId, failedAt desc).
      const db = initDb();
      const tasksCollection = collection(db, "tasks");
      const todayStart = nowInZoo(this.props.zoo).startOf("day").toDate();

      const qActive = query(
        tasksCollection,
        where("zooId", "==", zooId),
        where("status", "in", ["OPEN", "IN_PROGRESS"])
      );
      const qDoneToday = query(
        tasksCollection,
        where("status", "==", "DONE"),
        where("zooId", "==", zooId),
        where("completedAt", ">=", todayStart),
        orderBy("completedAt", "desc")
      );
      const qFailedToday = query(
        tasksCollection,
        where("status", "==", "FAILED"),
        where("zooId", "==", zooId),
        where("failedAt", ">=", todayStart),
        orderBy("failedAt", "desc")
      );

      const snapshots = await Promise.all([
        getDocs(qActive),
        getDocs(qDoneToday),
        getDocs(qFailedToday),
      ]);

      // Merge and deduplicate
      const allTasksMap = new Map();
      snapshots.forEach((snapshot) => {
        snapshot.forEach((doc) => {
          if (allTasksMap.has(doc.id)) return;
          const data = doc.data();
          if (data.status === "ARCHIVED" || data.status === "CANCELLED") return;
          allTasksMap.set(doc.id, { ...data, taskId: doc.id, id: doc.id });
        });
      });

      const allZooTasks = Array.from(allTasksMap.values());

      // Get user sectors
      const userSectors = sectorsData
        .filter(
          (sector) =>
            sector.sectorKeeper?.includes(userId) ||
            sector.managementUserIds?.includes(userId) ||
            sector.veterinaryUserIds?.includes(userId) ||
            sector.nutritionUserIds?.includes(userId)
        )
        .map((s) => s.sectorId);

      let allTasks = [];

      // Process all tasks and filter by user access ("My Tasks" logic)
      allZooTasks.forEach((task) => {
        // Critère 1 : Tâches assignées à moi
        const isAssignedToMe = task.responsibleIds?.includes(userId);

        // Critère 2 : Tâches publiques (sans responsables) ET dans mes secteurs
        const isPublic = !task.responsibleIds || task.responsibleIds.length === 0;
        const isInUserSector = task.sectorIds?.some((sectorId) =>
          userSectors.includes(sectorId)
        );

        // Critère 3 : Tâches en libre accès ET dans mes secteurs
        const isFreeAccess = task.assignmentType === 'free';

        // Afficher si AU MOINS UN critère est rempli
        if (isAssignedToMe || (isPublic && isInUserSector) || (isFreeAccess && isInUserSector)) {
          // Add sector name for display
          const sectorId = task.sectorIds?.[0];
          const sector = sectorsData.find((s) => s.sectorId === sectorId);
          task.sectorName = sector?.sectorName || this.props.t("task.noSector");
          task.sectorId = sectorId;

          allTasks.push(task);
        }
      });

      // Remove duplicates
      allTasks = Array.from(
        new Map(allTasks.map((t) => [t.taskId || t.id, t])).values()
      );

      // Filter tasks for TODAY according to tasks_time_guidelines.md
      // 3 criteria:
      // 1. Active tasks (OPEN/IN_PROGRESS) - shown regardless of date
      // 2. DONE/FAILED tasks whose validity period overlaps today
      // 3. DONE/FAILED tasks completed today

      const now = nowInZoo(this.props.zoo);
      const dayStart = nowInZoo(this.props.zoo).startOf("day");
      const dayEnd = nowInZoo(this.props.zoo).endOf("day");

      const tasksForToday = allTasks.filter((task) => {
        // Exclude ARCHIVED and CANCELLED (should already be filtered but double-check)
        if (task.status === "ARCHIVED" || task.status === "CANCELLED") {
          return false;
        }

        // Critère 1: Tâches actives dont la date de visibilité (scheduledFor) est passée
        if (task.status === "OPEN" || task.status === "IN_PROGRESS") {
          const scheduledFor = this.toMoment(task.scheduledFor);
          // Vérifier que scheduledFor est absent OU déjà passé
          const isVisible = !scheduledFor || scheduledFor.isSameOrBefore(dayEnd);
          if (isVisible) {
            return true;
          }
        }

        // Pour les tâches DONE/FAILED, on affiche UNIQUEMENT celles complétées aujourd'hui
        // (pas celles dont la période chevauche aujourd'hui mais clôturées avant)
        // Car le Header doit montrer les tâches PERTINENTES pour aujourd'hui
        const completedAt = this.toMoment(task.completedAt);
        const failedAt = this.toMoment(task.failedAt);
        const completionDate = task.status === "DONE" ? completedAt : failedAt;
        const isCompletedToday =
          (task.status === "DONE" || task.status === "FAILED") &&
          completionDate &&
          completionDate.isValid() &&
          completionDate.isBetween(dayStart, dayEnd, null, "[]");

        return isCompletedToday;
      });

      // Apply user notification preference for badge count
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const taskNotificationMode = userData.taskNotificationMode || "punctual_only";
      const notificationTasks = taskNotificationMode === "punctual_only"
        ? tasksForToday.filter((task) => task.taskCategory === "punctual")
        : tasksForToday;

      // Calculate stats based on filtered tasks for badge, but keep totalAll for dropdown
      const stats = {
        totalAll: tasksForToday.length,
        total: notificationTasks.length,
        open: notificationTasks.filter((t) => t.status === "OPEN" && (!this.toMoment(t.dueAt)?.isBefore(now))).length,
        openLate: notificationTasks.filter((t) => {
          const dueAt = this.toMoment(t.dueAt);
          return t.status === "OPEN" && dueAt && dueAt.isValid() && dueAt.isBefore(now);
        }).length,
        inProgress: notificationTasks.filter((t) => t.status === "IN_PROGRESS").length,
        done: notificationTasks.filter((t) => t.status === "DONE" && !t.completedLate).length,
        doneLate: notificationTasks.filter((t) => t.status === "DONE" && t.completedLate).length,
        failed: notificationTasks.filter((t) => t.status === "FAILED").length,
        overdue: notificationTasks.filter((t) => {
          // A task is overdue if dueAt is in the past and status is OPEN or IN_PROGRESS
          const dueAt = this.toMoment(t.dueAt);
          return (
            (t.status === "OPEN" || t.status === "IN_PROGRESS") &&
            dueAt &&
            dueAt.isValid() &&
            dueAt.isBefore(now)
          );
        }).length,
        dueToday: notificationTasks.filter((t) => {
          // A task is due today if its validity period overlaps today
          const scheduledFor = this.toMoment(t.scheduledFor);
          const dueAt = this.toMoment(t.dueAt);
          return (
            (!scheduledFor || scheduledFor.isSameOrBefore(dayEnd)) &&
            (!dueAt || dueAt.isSameOrAfter(dayStart)) &&
            (t.status === "OPEN" || t.status === "IN_PROGRESS")
          );
        }).length,
      };

      // Group ALL tasks for today by sector (dropdown shows everything)
      const groupedTasks = {};
      tasksForToday.forEach((task) => {
        const sectorName = task.sectorName || this.props.t("task.noSector");
        if (!groupedTasks[sectorName]) {
          groupedTasks[sectorName] = {
            sectorId: task.sectorId,
            tasks: [],
            stats: {
              total: 0,
              overdue: 0,
              inProgress: 0,
              open: 0,
            },
          };
        }

        groupedTasks[sectorName].tasks.push(task);
        groupedTasks[sectorName].stats.total++;

        // Check if task is overdue (dueAt in the past and still OPEN/IN_PROGRESS)
        const dueAt = this.toMoment(task.dueAt);
        const isOverdue = dueAt && dueAt.isValid() && dueAt.isBefore(now);

        if (task.status === "IN_PROGRESS") {
          groupedTasks[sectorName].stats.inProgress++;
        } else if (task.status === "OPEN") {
          // Only count as "open" if NOT overdue
          if (!isOverdue) {
            groupedTasks[sectorName].stats.open++;
          }
        }

        // Count overdue tasks (OPEN or IN_PROGRESS that are past due date)
        if ((task.status === "OPEN" || task.status === "IN_PROGRESS") && isOverdue) {
          groupedTasks[sectorName].stats.overdue++;
        }
      });

      this.setState({
        tasks: tasksForToday,
        sectors: sectorsData,
        stats,
        groupedTasks,
        loading: false,
      });
    } catch (error) {
      console.error("Error loading tasks:", error);
      this.setState({ loading: false });
    }
  };

  toMoment = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue && dateValue.toDate) {
      return moment(dateValue.toDate());
    }
    return moment(dateValue);
  };

  getPriorityColor = (task) => {
    const dueDate = this.toMoment(task.dueAt);
    if (!dueDate || !dueDate.isValid()) return "secondary";

    const now = nowInZoo(this.props.zoo);
    if (dueDate.isBefore(now)) return "danger";
    if (dueDate.isSame(now, "day")) return "warning";
    return "info";
  };

  getTaskIcon = (task) => {
    if (task.animalIds?.length > 0) return "paw";
    if (task.enclosureIds?.length > 0) return "border-all";
    return "layer-group";
  };

  renderTaskItem = (task) => {
    const dueDate = this.toMoment(task.dueAt);
    const isOverdue =
      dueDate && dueDate.isValid() && dueDate.isBefore(nowInZoo(this.props.zoo));
    const taskKey = task.taskId || task.id || task.docId;

    // Déterminer la couleur selon le statut et les guidelines
    let borderColor = TASK_STATUS_COLORS.OPEN.primary; // Bleu par défaut
    let backgroundColor = "white";

    if (task.status === "IN_PROGRESS") {
      borderColor = TASK_STATUS_COLORS.IN_PROGRESS.primary; // Cyan #06B6D4
      backgroundColor = TASK_STATUS_COLORS.IN_PROGRESS.light; // #CFFAFE
    } else if (isOverdue && task.status === "OPEN") {
      // Tâche ouverte en retard = OPEN_LATE (orange)
      borderColor = TASK_STATUS_COLORS.OPEN_LATE.primary; // Orange #F97316
      backgroundColor = TASK_STATUS_COLORS.OPEN_LATE.light; // #FFEDD5
    } else if (task.status === "OPEN") {
      borderColor = TASK_STATUS_COLORS.OPEN.primary; // Bleu #3B82F6
      backgroundColor = TASK_STATUS_COLORS.OPEN.light; // #DBEAFE
    } else if (task.status === "DONE") {
      borderColor = task.completedLate
        ? TASK_STATUS_COLORS.DONE_LATE.primary  // Kaki #B8B500
        : TASK_STATUS_COLORS.DONE.primary;      // Vert #10B981
      backgroundColor = task.completedLate
        ? TASK_STATUS_COLORS.DONE_LATE.light
        : TASK_STATUS_COLORS.DONE.light;
    } else if (task.status === "FAILED") {
      borderColor = TASK_STATUS_COLORS.FAILED.primary; // Rouge #EF4444
      backgroundColor = TASK_STATUS_COLORS.FAILED.light;
    }

    return (
      <div
        key={taskKey}
        className="px-3 py-2 task-notification-item"
        style={{
          borderLeft: `3px solid ${borderColor}`,
          backgroundColor: backgroundColor,
          cursor: "pointer",
        }}
      >
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <h6 className="mb-1 small font-weight-bold">
              <i
                className={`fas fa-${this.getTaskIcon(task)} mr-2 text-muted`}
              ></i>
              {task.title}
            </h6>
            {task.description && (
              <ClampedMarkdown
                lines={2}
                className="text-muted mb-1"
                style={{ fontSize: "0.75rem" }}
              >
                {task.description}
              </ClampedMarkdown>
            )}
            <div className="d-flex align-items-center">
              {task.status === "DONE" && (
                <small className="text-muted">
                  {this.props.t("task.status.DONE")}
                </small>
              )}
              {task.status === "FAILED" && (
                <small className="text-muted">
                  {this.props.t("task.status.FAILED")}
                </small>
              )}
              {task.status === "IN_PROGRESS" && (
                <small className="text-muted">
                  {this.props.t("task.status.IN_PROGRESS")}
                </small>
              )}
              {task.status === "OPEN" && isOverdue && (
                <small className="text-danger">
                  <i className="far fa-clock mr-1"></i>
                  {this.props.t("task.status.OPEN_LATE")}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderSectorGroup = (sectorName, sectorData) => {
    const { stats, tasks } = sectorData;
    const hasUrgent = stats.overdue > 0 || stats.inProgress > 0;
    const isExpanded = this.state.expandedSectors[sectorName] || false;

    // Filter to show only active tasks (OPEN and IN_PROGRESS)
    const activeTasks = tasks.filter((t) => t.status === "OPEN" || t.status === "IN_PROGRESS");
    const hasMoreTasks = activeTasks.length > 3;
    const tasksToShow = isExpanded ? activeTasks : activeTasks.slice(0, 3);

    // Count only OPEN (late + not late) and IN_PROGRESS tasks
    const activeTasksCount = stats.open + stats.overdue + stats.inProgress;

    return (
      <div key={sectorName} className="mb-3">
        <div
          className="px-3 py-2 bg-light d-flex justify-content-between align-items-center"
          style={{ borderRadius: "5px" }}
        >
          <div>
            <strong className="text-dark">{sectorName}</strong>
            <div className="mt-1">
              {stats.overdue > 0 && (
                <span className="badge task-badge-overdue mr-2">
                  {this.props.t("task.status.OPEN_LATE", {
                    count: stats.overdue,
                  })}
                </span>
              )}
              {stats.inProgress > 0 && (
                <span className="badge task-badge-in-progress mr-2">
                  {this.props.t("task.status.IN_PROGRESS", {
                    count: stats.inProgress,
                  })}
                </span>
              )}
              {stats.open > 0 && (
                <span className="badge task-badge-open">
                  {this.props.t("task.status.OPEN", { count: stats.open })}
                </span>
              )}
            </div>
          </div>
          <span
            className={`badge badge-pill ${hasUrgent ? 'task-badge-overdue' : ''}`}
            style={{
              backgroundColor: hasUrgent ? undefined : '#6B7280',
              color: 'white'
            }}
          >
            {activeTasksCount}
          </span>
        </div>

        {/* Show tasks (3 by default, all if expanded) */}
        <div className="mt-2">
          {tasksToShow.map((task) => this.renderTaskItem(task))}
          {hasMoreTasks && (
            <div
              className="text-center py-2"
              style={{ cursor: 'pointer' }}
              onClick={() => this.toggleSectorExpansion(sectorName)}
            >
              <small className="text-primary font-weight-bold">
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} mr-1`}></i>
                {isExpanded
                  ? this.props.t("task.showLess")
                  : this.props.t("task.otherTasks", { count: activeTasks.length - 3 })
                }
              </small>
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { tasksDestination = "/TaskManagementView", t } = this.props;
    const { stats, groupedTasks, loading } = this.state;
    // Show notification badge ONLY for active tasks (OPEN + IN_PROGRESS)
    const todayTasksCount = stats.open + stats.openLate + stats.inProgress;
    const urgentCount = stats.openLate; // Only overdue tasks are urgent
    const hasNotifications = todayTasksCount > 0;
    const hasAnyTasks = stats.totalAll > 0;

    return (
      <Dropdown
        nav
        inNavbar
        isOpen={this.state.dropdownOpen}
        toggle={this.toggleDropdown}
      >
        <DropdownToggle
          aria-label={t("navigationV2.header.tasks")}
          nav
          className="position-relative"
          style={{ padding: "0.5rem 1rem" }}
        >
          <em
            aria-hidden="true"
            className="fas fa-tasks"
            style={{
              fontSize: "1.3rem",
              color: "white",
            }}
          ></em>
          {hasNotifications && (
            <Badge
              color={urgentCount > 0 ? "danger" : "warning"}
              pill
              style={{
                position: "absolute",
                top: "2px",
                right: "2px",
                fontSize: "0.7rem",
                minWidth: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #fff",
                animation: urgentCount > 0 ? "task-notification-pulse 2s infinite" : "none",
              }}
            >
              {todayTasksCount > 99 ? "99+" : todayTasksCount}
            </Badge>
          )}
        </DropdownToggle>

        <DropdownMenu
          className="task-notification-menu"
          style={{
            width: "420px",
            maxHeight: "600px",
            overflowY: "auto",
            marginTop: "10px",
            right: "0",
            left: "auto",
            transform: "translateX(-10px)",
          }}
        >
          {/* Header */}
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 font-weight-bold">
                <i className="fas fa-clipboard-list mr-2"></i>
                {t("task.myTasks")}
              </h6>
              <Link
                to={tasksDestination}
                className="btn btn-sm btn-primary"
                onClick={this.closeDropdown}
              >
                {t("task.seeAll")}
              </Link>
            </div>
          </div>

          {/* Stats Summary */}
          {hasAnyTasks && (
            <div className="px-3 py-2 border-bottom">
              <Row className="text-center">
                <Col xs={3}>
                  <div>
                    <h5 className="mb-0" style={{ color: stats.openLate > 0 ? TASK_STATUS_COLORS.OPEN_LATE.primary : TASK_STATUS_COLORS.OPEN.primary }}>
                      {stats.open + stats.openLate}
                    </h5>
                    <small className="text-muted">
                      {t("task.status.OPEN")}
                    </small>
                  </div>
                </Col>
                <Col xs={3}>
                  <div>
                    <h5 className="mb-0" style={{ color: TASK_STATUS_COLORS.IN_PROGRESS.primary }}>{stats.inProgress}</h5>
                    <small className="text-muted">
                      {t("task.status.IN_PROGRESS")}
                    </small>
                  </div>
                </Col>
                <Col xs={3}>
                  <div>
                    <h5 className="mb-0" style={{ color: TASK_STATUS_COLORS.FAILED.primary }}>{stats.failed}</h5>
                    <small className="text-muted">
                      {t("task.status.FAILED")}
                    </small>
                  </div>
                </Col>
                <Col xs={3}>
                  <div>
                    <h5 className="mb-0" style={{ color: TASK_STATUS_COLORS.DONE.primary }}>
                      {stats.done + stats.doneLate}
                    </h5>
                    <small className="text-muted">
                      {t("task.status.DONE")}
                    </small>
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {/* Tasks grouped by sector */}
          <div
            className="py-2"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            {loading ? (
              <div className="text-center py-4">
                <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
              </div>
            ) : !hasAnyTasks ? (
              <div className="text-center py-4">
                <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                <p className="text-muted mb-0">{t("task.noTasks")}</p>
                <small className="text-muted">{t("task.upToDate")}</small>
              </div>
            ) : (
              Object.entries(groupedTasks)
                .filter(([, sectorData]) => {
                  // Only show sectors with active tasks (OPEN or IN_PROGRESS)
                  const activeTasksCount = sectorData.stats.open + sectorData.stats.overdue + sectorData.stats.inProgress;
                  return activeTasksCount > 0;
                })
                .map(([sectorName, sectorData]) =>
                  this.renderSectorGroup(sectorName, sectorData)
                )
            )}
          </div>

          {/* Footer */}
          <div className="border-top px-3 py-2 bg-light">
            <Link
              to={tasksDestination}
              className="btn btn-block btn-outline-primary btn-sm"
              onClick={this.closeDropdown}
            >
              <i className="fas fa-external-link-alt mr-2"></i>
              {t("task.seeAllTasks")}
            </Link>
          </div>
        </DropdownMenu>
      </Dropdown>
    );
  }
}

const mapStateToProps = (state) => ({
  zoo: state.zoo
});

export default withProductFeature("logbook.tasks")(connect(mapStateToProps)(withNamespaces("translations")(TaskNotificationDropdown)));
